/**
 * Geographic Filter Component
 * 
 * UI component for filtering opportunities by geographic location
 * Supports 55 cities and 6 provinces
 */

import React, { useState } from 'react';
import { Card, Button, Badge } from '@tracekit/core';
import { MapPin, X, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { GeographicFilter as GeoFilter } from '../utils/geographicFiltering';

export interface GeographicConfig {
  priorityCities: {
    bc: string[];
    ontario: string[];
    other: string[];
  };
  provinces: {
    name: string;
    code: string;
    priority: 'high' | 'medium' | 'low';
  }[];
  regions: {
    name: string;
    cities: string[];
    province: string;
  }[];
  cityToProvince: Record<string, string>;
  cityToRegion: Record<string, string>;
}

export interface GeographicFilterProps {
  config: GeographicConfig;
  filter: GeoFilter;
  onChange: (filter: GeoFilter) => void;
  onClear: () => void;
}

// Helper functions that use the config
function getAllProvinces(config: GeographicConfig) {
  return config.provinces;
}

function getAllCities(config: GeographicConfig): string[] {
  return Object.keys(config.cityToProvince);
}

function getPriorityCities(config: GeographicConfig): string[] {
  return [
    ...config.priorityCities.bc,
    ...config.priorityCities.ontario,
    ...config.priorityCities.other,
  ];
}

function getCitiesForProvince(config: GeographicConfig, provinceCode: string): string[] {
  return Object.entries(config.cityToProvince)
    .filter(([_, code]) => code === provinceCode)
    .map(([city, _]) => city);
}

export function GeographicFilter({ config, filter, onChange, onClear }: GeographicFilterProps) {
  const [expandedProvince, setExpandedProvince] = useState<string | null>(null);
  const [showAllCities, setShowAllCities] = useState(false);
  
  const provinces = getAllProvinces(config);
  const allCities = getAllCities(config);
  const priorityCities = getPriorityCities(config);
  const selectedCities = filter.cities || [];
  const selectedProvinces = filter.provinces || [];
  const selectedRegions = filter.regions || [];
  
  const handleProvinceToggle = (provinceCode: string) => {
    const newProvinces = selectedProvinces.includes(provinceCode)
      ? selectedProvinces.filter(p => p !== provinceCode)
      : [...selectedProvinces, provinceCode];
    onChange({ ...filter, provinces: newProvinces.length > 0 ? newProvinces : undefined });
  };
  
  const handleCityToggle = (city: string) => {
    const newCities = selectedCities.includes(city)
      ? selectedCities.filter(c => c !== city)
      : [...selectedCities, city];
    onChange({ ...filter, cities: newCities.length > 0 ? newCities : undefined });
  };
  
  const handleRegionToggle = (region: string) => {
    const newRegions = selectedRegions.includes(region)
      ? selectedRegions.filter(r => r !== region)
      : [...selectedRegions, region];
    onChange({ ...filter, regions: newRegions.length > 0 ? newRegions : undefined });
  };
  
  const handlePriorityOnlyToggle = () => {
    onChange({ ...filter, priorityOnly: !filter.priorityOnly });
  };
  
  const handleIncludeRemoteToggle = () => {
    onChange({ ...filter, includeRemote: !filter.includeRemote });
  };
  
  const handleSelectProvinceCities = (provinceCode: string) => {
    const provinceCities = getCitiesForProvince(config, provinceCode);
    const newCities = [...new Set([...selectedCities, ...provinceCities])];
    onChange({ ...filter, cities: newCities });
  };
  
  const displayCities = filter.priorityOnly ? priorityCities : allCities;
  const visibleCities = showAllCities ? displayCities : displayCities.slice(0, 20);
  
  const hasActiveFilters = 
    (selectedProvinces.length > 0) ||
    (selectedCities.length > 0) ||
    (selectedRegions.length > 0) ||
    filter.priorityOnly ||
    filter.includeRemote;
  
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Geographic Filter</h3>
          {hasActiveFilters && (
            <Badge variant="primary" className="ml-2">
              {selectedProvinces.length + selectedCities.length + selectedRegions.length} active
            </Badge>
          )}
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
      
      {/* Quick Options */}
      <div className="mb-4 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filter.priorityOnly || false}
            onChange={handlePriorityOnlyToggle}
            className="rounded"
          />
          <span className="text-sm">Priority cities only (BC & Ontario)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filter.includeRemote || false}
            onChange={handleIncludeRemoteToggle}
            className="rounded"
          />
          <span className="text-sm">Include remote locations (First Nations, fly-in communities)</span>
        </label>
      </div>
      
      {/* Regions */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold mb-2">Regions</h4>
        <div className="flex flex-wrap gap-2">
          {config.regions.map(region => (
            <button
              key={region.name}
              onClick={() => handleRegionToggle(region.name)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                selectedRegions.includes(region.name)
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
              }`}
            >
              {region.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Provinces */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold mb-2">Provinces (6)</h4>
        <div className="space-y-2">
          {provinces.map(province => {
            const isSelected = selectedProvinces.includes(province.code);
            const provinceCities = getCitiesForProvince(config, province.code);
            const isExpanded = expandedProvince === province.code;
            
            return (
              <div key={province.code} className="border rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleProvinceToggle(province.code)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">
                      {province.name} ({provinceCities.length} cities)
                    </span>
                    <Badge 
                      variant={province.priority === 'high' ? 'primary' : 'secondary'}
                      className="text-xs"
                    >
                      {province.priority}
                    </Badge>
                  </label>
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSelectProvinceCities(province.code)}
                        className="text-xs"
                      >
                        Select All Cities
                      </Button>
                    )}
                    <button
                      onClick={() => setExpandedProvince(isExpanded ? null : province.code)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-2 pl-6">
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {provinceCities.map(city => (
                        <button
                          key={city}
                          onClick={() => handleCityToggle(city)}
                          className={`px-2 py-1 rounded text-xs border transition-colors ${
                            selectedCities.includes(city)
                              ? 'bg-blue-100 border-blue-500 text-blue-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                          }`}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Cities */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">
            Cities ({filter.priorityOnly ? priorityCities.length : allCities.length})
          </h4>
          {!filter.priorityOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllCities(!showAllCities)}
            >
              {showAllCities ? 'Show Less' : `Show All (${allCities.length})`}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
          {visibleCities.map(city => (
            <button
              key={city}
              onClick={() => handleCityToggle(city)}
              className={`px-2 py-1 rounded text-xs border transition-colors ${
                selectedCities.includes(city)
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
              }`}
            >
              {city}
            </button>
          ))}
        </div>
        {!showAllCities && displayCities.length > 20 && (
          <p className="text-xs text-gray-500 mt-2">
            Showing 20 of {displayCities.length} cities. Click "Show All" to see more.
          </p>
        )}
      </div>
    </Card>
  );
}

